import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Plus } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { serviceImage, serviceTint } from '@/lib/domain';
import { formatSlot } from '@/lib/format';
import { useUserId } from '@/components/AuthProvider';
import { EmptyState, HeroHeader, StatusBadge } from '@/components/ui';
import Spinner from '@/components/Spinner';
import styles from './Bookings.module.css';

type BookingRow = {
  id: string;
  status: string;
  address: string;
  preferred_date: string | null;
  preferred_time: string | null;
  service_name: string;
  proposal_count: number;
};

export default function ClientBookings() {
  const userId = useUserId();
  const [bookings, setBookings] = useState<BookingRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select('id, status, address, preferred_date, preferred_time, services(name)')
        .eq('client_id', userId)
        .order('created_at', { ascending: false });

      if (!error && data) {
        // Pending proposals per open booking, so the client sees news without opening each card.
        const pendingIds = data
          .filter((row: any) => row.status === 'pending')
          .map((row: any) => row.id);
        const proposalCounts = new Map<string, number>();
        if (pendingIds.length > 0) {
          const { data: proposals } = await supabase
            .from('booking_proposals')
            .select('booking_id')
            .in('booking_id', pendingIds)
            .eq('status', 'pending');
          (proposals ?? []).forEach(({ booking_id }: any) => {
            proposalCounts.set(booking_id, (proposalCounts.get(booking_id) ?? 0) + 1);
          });
        }

        setBookings(
          data.map((row: any) => ({
            id: row.id,
            status: row.status,
            address: row.address,
            preferred_date: row.preferred_date,
            preferred_time: row.preferred_time,
            service_name: row.services?.name ?? 'Service',
            proposal_count: proposalCounts.get(row.id) ?? 0,
          }))
        );
      }
    } catch (err) {
      console.error('Error loading bookings:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className={styles.page}>
      <HeroHeader title="Mes demandes" subtitle="Suivez l'avancement de vos services.">
        <Link className={`btn btn--on-brand ${styles.newBtn}`} to="/app/new-request">
          <Plus size={18} /> Nouvelle demande
        </Link>
      </HeroHeader>

      <div className={styles.body}>
        {loading ? (
          <Spinner block />
        ) : bookings.length === 0 ? (
          <EmptyState
            illustration="/assets/illustrations/clipboard.png"
            title="Aucune demande"
            text="Créez votre première demande de service et recevez des propositions de prestataires."
            action={
              <Link className="btn btn--primary" to="/app/new-request">
                Créer une demande
              </Link>
            }
          />
        ) : (
          <ul className={styles.list}>
            {bookings.map((booking) => (
              <li key={booking.id}>
                <Link
                  className={styles.card}
                  to={`/app/bookings/${booking.id}${booking.proposal_count > 0 ? '#propositions' : ''}`}
                >
                  <span className={styles.cardArt} style={{ background: serviceTint(booking.service_name) }}>
                    <img src={serviceImage(booking.service_name)} alt="" />
                  </span>
                  <div className={styles.cardInfo}>
                    <p className={styles.serviceName}>{booking.service_name}</p>
                    <p className={styles.meta}>
                      <MapPin size={13} strokeWidth={2.1} />
                      <span className={styles.metaText}>{booking.address}</span>
                    </p>
                    <p className={styles.meta}>
                      <Calendar size={13} strokeWidth={2.1} />
                      <span className={styles.metaText}>
                        {formatSlot(booking.preferred_date, booking.preferred_time)}
                      </span>
                    </p>
                    {booking.proposal_count > 0 ? (
                      <span className={styles.proposalBadge}>
                        {booking.proposal_count} proposition{booking.proposal_count > 1 ? 's' : ''}{' '}
                        à consulter
                      </span>
                    ) : null}
                  </div>
                  <StatusBadge status={booking.status} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
